import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Tracks which inbox rows changed since the agent last looked, so the table can
 * call attention to them.
 *
 * The inbox re-sorts on every poll, so a conversation that receives a reply silently
 * jumps to the top. Unread styling alone doesn't cover it — an already-read
 * conversation that gets a new reply, or one bumped by an update, looks identical to
 * the rows around it and the movement is easy to miss entirely.
 *
 * Two kinds of change are worth their own label, and they stack rather than
 * replace each other: a row that wasn't in the list before is a newly created
 * ticket (`isNew`), and a row whose timestamp moved got a reply (`hasReply`). A
 * brand-new ticket the customer then replies to carries both — it is still the
 * new ticket in the list, and it now also has an answer waiting. The very first
 * load is excluded: on mount every row would qualify and the whole table would
 * light up.
 *
 * A mark has no expiry. It is the "you haven't seen this yet" signal, so the only
 * thing that retires it is the agent actually seeing the row — `clear()` on open,
 * or the row going read. A timed fade meant a reply that landed while the agent
 * was in another tab was already gone by the time they came back.
 *
 * `resetKey` identifies *which* list is being shown (scope + filter + search +
 * page …). Changing it swaps the list wholesale, so the incoming rows are all
 * unrelated to the outgoing ones — that re-establishes the baseline silently
 * instead of flagging a whole new page as freshly arrived.
 */

export type UpdateKind = 'new' | 'reply'

/** What has happened to a row since the agent last saw the list. Both flags can
 *  be set at once — a new ticket that has since been replied to. */
export interface RowMark {
  isNew: boolean
  hasReply: boolean
}

export interface TrackableRow {
  id: string
  timestamp: string
}

export interface RecentlyUpdated {
  /** Row id → what happened to it, for every row still carrying a mark. */
  marks: Map<string, RowMark>
  /** Drops a row's highlight immediately — the agent opened it, so the
   *  "you haven't seen this yet" signal has served its purpose. */
  clear: (id: string) => void
}

export function useRecentlyUpdated(rows: TrackableRow[], resetKey = ''): RecentlyUpdated {
  const [recent, setRecent] = useState<Map<string, RowMark>>(new Map())
  const seenRef = useRef<Map<string, string> | null>(null)
  const keyRef = useRef(resetKey)

  useEffect(() => {
    const viewChanged = keyRef.current !== resetKey
    const previous = viewChanged ? null : seenRef.current
    keyRef.current = resetKey
    const next = new Map(rows.map((r) => [r.id, r.timestamp]))

    // First load — and every wholesale swap of the list — establishes a fresh
    // baseline; nothing is "new" relative to it.
    if (previous === null) {
      seenRef.current = next
      setRecent((prev) => (prev.size === 0 ? prev : new Map()))
      return
    }

    const changed: [string, UpdateKind][] = []
    for (const row of rows) {
      const before = previous.get(row.id)
      if (before === undefined) changed.push([row.id, 'new'])
      else if (before !== row.timestamp) changed.push([row.id, 'reply'])
    }
    seenRef.current = next

    setRecent((prev) => {
      // Marks live until the row is seen, so they'd otherwise pile up for rows
      // that have long since left the list — drop those on every pass.
      const stale = [...prev.keys()].filter((id) => !next.has(id))
      if (changed.length === 0 && stale.length === 0) return prev

      const merged = new Map(prev)
      for (const id of stale) merged.delete(id)
      for (const [id, kind] of changed) {
        const before = merged.get(id) ?? { isNew: false, hasReply: false }
        merged.set(id, {
          // Once flagged new, a row stays new until it's opened — a reply
          // arriving on top of it adds the reply badge, it doesn't replace one.
          isNew: before.isNew || kind === 'new',
          hasReply: before.hasReply || kind === 'reply',
        })
      }
      return merged
    })
  }, [rows, resetKey])

  const clear = useCallback((id: string): void => {
    setRecent((prev) => {
      if (!prev.has(id)) return prev
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  return { marks: recent, clear }
}
